import React from 'react';
import { useIntl, useSelector } from 'umi';
import Loading from '@/pages/dashboard/components/Loadings';
import { IframeUrl } from '@/pages/home/components/IframeUrl';
import { getUtilAuthToken } from '@/pages/home/services/authPath';
import { getUserDetail } from '@/pages/home/services/home';
import { buriedBtnPoint } from '@/pages/home/utils/buriedPoint';
import useAuthPathReport, {
  type IBaseAuthMenu,
  type TAuthPathErrorKey,
} from '@/pages/home/hooks/useAuthPathReport';
import { createAuthPathRpcHandler } from '@home/components/AuthPathReport/rpc';
import NoData from '@op/components/NoDataCommon';
import './index.scss';

interface IAuthPathReportMenu extends IBaseAuthMenu {
  id?: string | number;
  u_id?: string | number;
  page_type?: string;
  path?: string;
  auth_path?: string;
  name_en?: string;
}

interface IAuthPathReportProps {
  pageId?: string;
  reportId?: string;
  currentMenu?: IAuthPathReportMenu | null;
  resId?: string;
  reportTypeId: string;
  getAuthPath?: (menu: IAuthPathReportMenu) => string;
  getMenuId?: (menu: IAuthPathReportMenu) => string;
  getPageType?: (menu: IAuthPathReportMenu) => string;
  className?: string;
  style?: React.CSSProperties;
  errorMessages?: Partial<Record<TAuthPathErrorKey, string>>;
}

const DEFAULT_ERROR_MESSAGES: Record<TAuthPathErrorKey, string> = {
  authpath_error_no_report: 'Failed to retrieve report information. If you have any questions, please contact {email}',
  authpath_error_no_game: 'Failed to retrieve game information. If you have any questions, please contact {email}',
  authpath_error_menu_mismatch: 'Menu mismatch detected. If you have any questions, please contact {email}',
  authpath_error_no_url: 'Failed to retrieve report URL. If you have any questions, please contact {email}',
  authpath_error_token_empty: 'Failed to obtain authentication token. If you have any questions, please contact {email}',
  authpath_error_fetch_failed: 'Failed to fetch authentication information. If you have any questions, please contact {email}',
};

const AuthPathReport: React.FC<IAuthPathReportProps> = ({
  pageId,
  reportId,
  currentMenu,
  resId = 'common',
  reportTypeId,
  getAuthPath = (menu) => menu.auth_path || menu.path || '',
  getMenuId = (menu) => String(menu.id || menu.u_id || ''),
  getPageType = (menu) => menu.page_type || 'auth_path',
  className,
  style,
  errorMessages,
}) => {
  const { formatMessage } = useIntl();
  const openid = useSelector((state: any) => state?.home?.userInfo?.openid || '');
  const mergedErrors = { ...DEFAULT_ERROR_MESSAGES, ...errorMessages };
  const handleMessage = createAuthPathRpcHandler({
    openid,
    typeId: reportTypeId,
    getUserInfo: getUserDetail,
    reportButtonLog: (btnLogCfg) => {
      buriedBtnPoint({ ...btnLogCfg, pageId: pageId || '' });
    },
  });

  const { loading, errorKey, authUrl } = useAuthPathReport<IAuthPathReportMenu, never>({
    reportId,
    currentMenu,
    currentGame: null,
    fetchToken: getUtilAuthToken,
    getAuthPath,
    getMenuId,
    getPageType,
    buildTokenParams: ({ authPath, menuId, pageType }) => ({
      res_id: resId,
      menu_id: menuId,
      page_type: pageType,
      page_value: authPath,
    }),
  });

  if (loading) return <Loading />;

  if (errorKey) {
    console.log(errorKey, 'errorKey', mergedErrors[errorKey]);
    return (
      <NoData
        className={`dbo-authpath-report`}
        style={style}
        emptyDescText={mergedErrors[errorKey]}
        hasDesc
        hasImg
      />
    );
  }

  return (
    <IframeUrl
      inheritQueryParams
      url={authUrl}
      className={className}
      style={style}
      onMessage={handleMessage}
    />
  );
};

export default AuthPathReport;
